import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Calendar } from 'primereact/calendar'
import './MovementFilterPanel.css'

const EMPTY_FORM = {
    prefix:        '',
    number:        '',
    equipmentType: '',
    length:        '',
    status:        '',
    date:          null,
    comments:      '',
    movement:      '',
    line:          '',
    vessel:        '',
    voyage:        '',
    from:          '',
    to:            '',
    final:         '',
}

const FIELD_MAP = {
    prefix:        { field: 'Equipment.Prefix',             type: 'string' },
    number:        { field: 'Equipment.Number',             type: 'int32'  },
    equipmentType: { field: 'Equipment.EquipmentType.Code', type: 'string' },
    length:        { field: 'Equipment.EquipmentType.Name', type: 'string' },
    status:        { field: 'Status',                       type: 'string' },
    date:          { field: 'Date',                         type: 'date'   },
    comments:      { field: 'Comments',                     type: 'string' },
    movement:      { field: 'MovementType.Code',            type: 'string' },
    line:          { field: 'AccountLine',                  type: 'string' },
    vessel:        { field: 'Transport.Name',               type: 'string' },
    voyage:        { field: 'VoyageNumber',                 type: 'string' },
    from:          { field: 'MovementFromCode',             type: 'string' },
    to:            { field: 'MovementToCode',               type: 'string' },
    final:         { field: 'MovementFinalCode',            type: 'string' },
}

const FIELDS = [
    { key: 'prefix',        label: 'Prefix'    },
    { key: 'number',        label: 'Number'    },
    { key: 'equipmentType', label: 'Eq. Type'  },
    { key: 'length',        label: 'Length'    },
    { key: 'status',        label: 'Status'    },
    { key: 'date',          label: 'Date'      },
    { key: 'comments',      label: 'Comments'  },
    { key: 'movement',      label: 'Movement'  },
    { key: 'line',          label: 'Line'      },
    { key: 'vessel',        label: 'Vessel'    },
    { key: 'voyage',        label: 'Voyage'    },
    { key: 'from',          label: 'From'      },
    { key: 'to',            label: 'To'        },
    { key: 'final',         label: 'Final'     },
]

const formatDate = (date) => {
    if (!date) return ''
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
}

/**
 * Builds the filter string from both columns.
 *
 * Special case — Date field:
 *   From column date → GTE operator (date >= fromDate)
 *   To   column date → LTE operator (date <= toDate)
 *   This produces a proper date range instead of two conflicting AND clauses.
 *
 * All other fields: each column contributes independently via contains() AND-ed together.
 */
const buildFilterString = (fromForm, toForm) => {
    const parts = []

    Object.entries(FIELD_MAP).forEach(([key, { field, type }]) => {
        if (type === 'date') {
            // Date is special — range across the two columns
            const fromDate = formatDate(fromForm[key])
            const toDate   = formatDate(toForm[key])
            if (fromDate) parts.push(`${field} gte '${fromDate}'`)
            if (toDate)   parts.push(`${field} lte '${toDate}'`)
            return
        }

        const fromVal = fromForm[key]?.trim()
        const toVal   = toForm[key]?.trim()

        if (fromVal) {
            parts.push(type === 'int32'
                ? `${field} eq ${fromVal}`
                : `contains(${field}, '${fromVal}')`)
        }
        if (toVal) {
            parts.push(type === 'int32'
                ? `${field} eq ${toVal}`
                : `contains(${field}, '${toVal}')`)
        }
    })

    return parts.join(' and ')
}

export const M_MovementFilter = ({ onFilterChange, visibleFields }) => {
    const { t } = useTranslation()

    const [fromForm, setFromForm] = useState(EMPTY_FORM)
    const [toForm,   setToForm]   = useState(EMPTY_FORM)
    const [isCollapsed, setIsCollapsed] = useState(false)
    const [isDirty, setIsDirty] = useState(false)
    const [hasActiveFilters, setHasActiveFilters] = useState(false)

    const fieldsToShow = visibleFields
        ? FIELDS.filter(f => visibleFields.includes(f.key))
        : FIELDS

    const handleChange = useCallback((side, key, value) => {
        if (side === 'from') {
            setFromForm(prev => ({ ...prev, [key]: value }))
        } else {
            setToForm(prev => ({ ...prev, [key]: value }))
        }
        setIsDirty(true)
    }, [])

    const handleSearch = () => {
        const filterString = buildFilterString(fromForm, toForm)
        onFilterChange(filterString)
        setIsDirty(false)
        setHasActiveFilters(!!filterString)
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSearch()
    }

    const handleClear = () => {
        setFromForm(EMPTY_FORM)
        setToForm(EMPTY_FORM)
        onFilterChange('')
        setIsDirty(false)
        setHasActiveFilters(false)
    }

    return (
        <div className="movement-filter-panel">

            {/* ── Header ── */}
            <div
                className={`movement-filter-header ${isCollapsed ? 'collapsed' : ''}`}
                onClick={() => setIsCollapsed(p => !p)}
            >
                <div className="movement-filter-header-left">
                    <i className='pi pi-filter' style={{ fontSize: '0.9rem', color: '#6c757d' }} />
                    <span className="movement-filter-header-title">{t('Filters')}</span>
                    {hasActiveFilters && !isDirty && (
                        <span className="movement-filter-active-badge">{t('Active')}</span>
                    )}
                    {isDirty && (
                        <span className="movement-filter-dirty-badge">{t('Unsaved')}</span>
                    )}
                </div>
                <div className="movement-filter-header-right">
                    {hasActiveFilters && (
                        <button
                            className="movement-filter-clear-btn"
                            onClick={e => { e.stopPropagation(); handleClear() }}
                        >
                            {t('Clear')}
                        </button>
                    )}
                    <i
                        className={`pi ${isCollapsed ? 'pi-chevron-down' : 'pi-chevron-up'}`}
                        style={{ fontSize: '0.8rem', color: '#6c757d' }}
                    />
                </div>
            </div>

            {/* ── Body: From column | To column ── */}
            {!isCollapsed && (
                <>
                    <div className="movement-filter-body">
                        {[
                            { side: 'from', heading: t('From'), form: fromForm },
                            { side: 'to',   heading: t('To'),   form: toForm   },
                        ].map(({ side, heading, form }) => (
                            <div key={side} className={`movement-filter-column ${side}`}>
                                <div className="movement-filter-column-header">{heading}</div>
                                <div className="movement-filter-fields">
                                    {fieldsToShow.map(({ key, label }) => {
                                        const { type } = FIELD_MAP[key]
                                        const val = form[key]
                                        return (
                                            <div key={key} className="movement-filter-field">
                                                <label className="movement-filter-label">{t(label)}</label>
                                                {type === 'date' ? (
                                                    <Calendar
                                                        value={val}
                                                        onChange={e => handleChange(side, key, e.value)}
                                                        dateFormat="yy-mm-dd"
                                                        placeholder={side === 'from' ? t('From date') : t('To date')}
                                                        showIcon
                                                        showButtonBar
                                                        inputClassName={`movement-filter-input ${val ? 'active' : ''}`}
                                                        className="movement-filter-calendar"
                                                        panelClassName="movement-filter-calendar-panel"
                                                    />
                                                ) : (
                                                    <input
                                                        className={`movement-filter-input ${val ? 'active' : ''}`}
                                                        value={val}
                                                        onChange={e => handleChange(side, key, e.target.value)}
                                                        onKeyDown={handleKeyDown}
                                                        placeholder={t(label)}
                                                    />
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* ── Footer ── */}
                    <div className="movement-filter-footer">
                        <button
                            className={`movement-filter-search-btn ${isDirty ? 'dirty' : ''}`}
                            onClick={handleSearch}
                        >
                            <i className="pi pi-search" style={{ fontSize: '0.85rem' }} />
                            {t('Search')}
                        </button>
                    </div>
                </>
            )}
        </div>
    )
}